/**
 * Error log store for persisting error/warning history
 * @module errorLogStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ErrorLogEntry, ErrorSeverity, ErrorSource } from '@/types/errorLog';

const SERVICE_TAG = '[ERROR_LOG]';
const MAX_ENTRIES = 100;

/**
 * Error log state
 */
interface ErrorLogState {
  /** Error log entries (newest first) */
  entries: ErrorLogEntry[];
  /** Count of unacknowledged errors */
  unacknowledgedCount: number;
}

/**
 * Filter options for querying entries
 */
interface EntryFilters {
  severity?: ErrorSeverity;
  source?: ErrorSource;
  acknowledged?: boolean;
}

/**
 * Error log actions
 */
interface ErrorLogActions {
  /** Add a new error entry, returns the entry ID */
  addEntry: (entry: Omit<ErrorLogEntry, 'id' | 'timestamp' | 'acknowledged'>) => string;
  /** Mark entry as acknowledged */
  acknowledgeEntry: (id: string) => void;
  /** Mark all entries as acknowledged */
  acknowledgeAll: () => void;
  /** Remove a specific entry */
  removeEntry: (id: string) => void;
  /** Clear all entries */
  clearAll: () => void;
  /** Get entries filtered by severity/source */
  getFilteredEntries: (filters: EntryFilters) => ErrorLogEntry[];
}

type ErrorLogStore = ErrorLogState & ErrorLogActions;

/**
 * Initial state
 */
const initialState: ErrorLogState = {
  entries: [],
  unacknowledgedCount: 0
};

/**
 * Error log store with localStorage persistence
 */
export const useErrorLogStore = create<ErrorLogStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addEntry: (partialEntry) => {
        const id = crypto.randomUUID();
        const entry: ErrorLogEntry = {
          ...partialEntry,
          id,
          timestamp: new Date().toISOString(),
          acknowledged: false
        };

        set((state) => {
          // Prepend new entry, keep max entries (FIFO)
          const newEntries = [entry, ...state.entries].slice(0, MAX_ENTRIES);
          const unacknowledgedCount = newEntries.filter((e) => !e.acknowledged).length;

          console.info(`${SERVICE_TAG} Added entry`, {
            id,
            source: entry.source,
            severity: entry.severity,
            totalEntries: newEntries.length
          });

          return { entries: newEntries, unacknowledgedCount };
        });

        return id;
      },

      acknowledgeEntry: (id) => {
        set((state) => {
          const entries = state.entries.map((e) =>
            e.id === id ? { ...e, acknowledged: true } : e
          );
          const unacknowledgedCount = entries.filter((e) => !e.acknowledged).length;
          return { entries, unacknowledgedCount };
        });
      },

      acknowledgeAll: () => {
        set((state) => ({
          entries: state.entries.map((e) => ({ ...e, acknowledged: true })),
          unacknowledgedCount: 0
        }));
      },

      removeEntry: (id) => {
        set((state) => {
          const entries = state.entries.filter((e) => e.id !== id);
          const unacknowledgedCount = entries.filter((e) => !e.acknowledged).length;
          return { entries, unacknowledgedCount };
        });
      },

      clearAll: () => {
        console.info(`${SERVICE_TAG} Clearing all entries`);
        set({ entries: [], unacknowledgedCount: 0 });
      },

      getFilteredEntries: (filters) => {
        const { entries } = get();
        return entries.filter((entry) => {
          if (filters.severity && entry.severity !== filters.severity) return false;
          if (filters.source && entry.source !== filters.source) return false;
          if (filters.acknowledged !== undefined && entry.acknowledged !== filters.acknowledged) {
            return false;
          }
          return true;
        });
      }
    }),
    {
      name: 'dicecapture-error-log',
      partialize: (state) => ({
        entries: state.entries,
        unacknowledgedCount: state.unacknowledgedCount
      })
    }
  )
);
